import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, Mail, Phone, MapPin, Instagram, Facebook, Youtube, Music,
  ChevronDown, Star, Check, Calendar, Send, ArrowRight, Menu, X, ExternalLink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${API}/api`;

// Font families for each font_style option
const FONT_MAP = {
  elegant: "'Playfair Display', Georgia, serif",
  modern: "'Inter', 'Helvetica Neue', sans-serif",
  fun: "'Poppins', 'Comic Sans MS', cursive",
  classic: "'Georgia', 'Times New Roman', serif",
};

const HEADING_FONT_MAP = {
  elegant: "'Playfair Display', Georgia, serif",
  modern: "'Inter', 'Helvetica Neue', sans-serif",
  fun: "'Poppins', 'Comic Sans MS', cursive",
  classic: "'Georgia', 'Times New Roman', serif",
};

export default function PublicWebsite() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', event_date: '', event_type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  const sectionRefs = useRef({});

  useEffect(() => {
    fetchSite();
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = window.scrollY + 100;
      const sections = Object.entries(sectionRefs.current);
      for (let i = sections.length - 1; i >= 0; i--) {
        const [id, el] = sections[i];
        if (el && el.offsetTop <= scrollPos) {
          setActiveSection(id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [data]);

  const fetchSite = async () => {
    try {
      const res = await axios.get(`${API_BASE}/site/${slug}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'not_found' : 'error');
    } finally {
      setLoading(false);
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/site/${slug}/contact`, contactForm);
      setSubmitted(true);
    } catch {
      alert('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollTo = (id) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileMenu(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
      <Loader2 className="w-10 h-10 animate-spin text-white/20" />
    </div>
  );

  if (error === 'not_found') return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f] text-white px-4">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold">404</h1>
        <p className="text-white/50">This website doesn't exist or hasn't been published yet.</p>
      </div>
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f] text-white px-4">
      <div className="text-center space-y-4">
        <h1 className="text-2xl font-bold">Something went wrong</h1>
        <p className="text-white/50">Please try again later.</p>
      </div>
    </div>
  );

  const { site, packages, addons, event_types } = data;
  const sc = site.sections_config || {};
  const isEnabled = (id) => sc[id]?.enabled !== false;
  const primary = site.primary_color || '#e91e63';
  const secondary = site.secondary_color || '#1a1a2e';
  const accent = site.accent_color || '#ffc107';
  const fontStyle = site.font_style || 'elegant';
  const bodyFont = FONT_MAP[fontStyle];
  const headingFont = HEADING_FONT_MAP[fontStyle];
  const socials = site.social_links || {};
  const template = site.template || 'elegant';

  // Visible nav sections
  const navSections = [
    isEnabled('about') && { id: 'about', label: 'About' },
    isEnabled('packages') && packages.length > 0 && { id: 'packages', label: 'Packages' },
    isEnabled('addons') && addons.length > 0 && { id: 'addons', label: 'Add-Ons' },
    isEnabled('gallery') && (site.gallery_images || []).length > 0 && { id: 'gallery', label: 'Gallery' },
    isEnabled('testimonials') && (site.testimonials || []).length > 0 && { id: 'testimonials', label: 'Reviews' },
    isEnabled('contact') && { id: 'contact', label: 'Contact' },
  ].filter(Boolean);

  // Dynamic styles based on template
  const templateStyles = {
    elegant: {
      navBg: `${secondary}ee`,
      sectionBg: secondary,
      sectionAltBg: `${secondary}dd`,
      cardBg: 'rgba(255,255,255,0.04)',
      cardBorder: 'rgba(255,255,255,0.08)',
      textMain: '#ffffff',
      textMuted: 'rgba(255,255,255,0.55)',
      heroOverlay: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%)',
    },
    modern: {
      navBg: '#0f172aee',
      sectionBg: '#0f172a',
      sectionAltBg: '#1e293b',
      cardBg: 'rgba(255,255,255,0.05)',
      cardBorder: 'rgba(255,255,255,0.1)',
      textMain: '#f8fafc',
      textMuted: 'rgba(248,250,252,0.5)',
      heroOverlay: 'linear-gradient(to bottom, rgba(15,23,42,0.4) 0%, rgba(15,23,42,0.85) 100%)',
    },
    party: {
      navBg: '#1a1a2eee',
      sectionBg: '#1a1a2e',
      sectionAltBg: '#2d1b69',
      cardBg: 'rgba(255,255,255,0.06)',
      cardBorder: 'rgba(255,255,255,0.1)',
      textMain: '#ffffff',
      textMuted: 'rgba(255,255,255,0.5)',
      heroOverlay: `linear-gradient(135deg, ${primary}33 0%, rgba(0,0,0,0.7) 100%)`,
    },
    classic: {
      navBg: '#1c1917ee',
      sectionBg: '#1c1917',
      sectionAltBg: '#292524',
      cardBg: 'rgba(255,255,255,0.04)',
      cardBorder: 'rgba(255,255,255,0.08)',
      textMain: '#fafaf9',
      textMuted: 'rgba(250,250,249,0.5)',
      heroOverlay: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(28,25,23,0.9) 100%)',
    },
  };

  const ts = templateStyles[template] || templateStyles.elegant;

  const resolveUrl = (url) => {
    if (!url) return '';
    return url.startsWith('/') ? `${API}${url}` : url;
  };

  // Social icons
  const socialIcons = [
    socials.instagram && { url: socials.instagram, icon: Instagram, label: 'Instagram' },
    socials.facebook && { url: socials.facebook, icon: Facebook, label: 'Facebook' },
    socials.youtube && { url: socials.youtube, icon: Youtube, label: 'YouTube' },
    socials.tiktok && { url: socials.tiktok, icon: Music, label: 'TikTok' },
  ].filter(Boolean);

  return (
    <div style={{ fontFamily: bodyFont, background: ts.sectionBg, color: ts.textMain, minHeight: '100vh' }} data-testid="public-website">
      {/* Google Fonts */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Inter:wght@300;400;500;600;700&family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* ─── Navigation ─── */}
      <nav
        style={{ background: ts.navBg, backdropFilter: 'blur(20px)', borderBottom: `1px solid ${ts.cardBorder}` }}
        className="fixed top-0 left-0 right-0 z-50"
        data-testid="site-nav"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {site.logo_url && (
              <img src={resolveUrl(site.logo_url)} alt="" className="h-8 w-auto object-contain" />
            )}
            <span className="font-semibold text-lg" style={{ fontFamily: headingFont }}>{site.site_title}</span>
          </div>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-6">
            {navSections.map(s => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className="text-sm font-medium transition-colors"
                style={{ color: activeSection === s.id ? primary : ts.textMuted }}
                data-testid={`nav-link-${s.id}`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Mobile menu toggle */}
          <button
            className="md:hidden p-2 rounded-lg"
            style={{ color: ts.textMuted }}
            onClick={() => setMobileMenu(!mobileMenu)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile nav */}
        {mobileMenu && (
          <div className="md:hidden px-4 pb-4 space-y-2" style={{ background: ts.navBg }}>
            {navSections.map(s => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className="block w-full text-left py-2 text-sm font-medium"
                style={{ color: activeSection === s.id ? primary : ts.textMuted }}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* ─── Hero ─── */}
      <section
        ref={el => (sectionRefs.current.hero = el)}
        className="relative flex items-center justify-center text-center overflow-hidden"
        style={{ minHeight: '85vh' }}
        data-testid="hero-section"
      >
        {site.hero_image_url ? (
          <img
            src={resolveUrl(site.hero_image_url)}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${secondary}, ${primary}22)` }} />
        )}
        <div className="absolute inset-0" style={{ background: ts.heroOverlay }} />
        <div className="relative z-10 px-4 max-w-3xl mx-auto space-y-6">
          {site.logo_url && (
            <img src={resolveUrl(site.logo_url)} alt="" className="h-16 sm:h-20 w-auto mx-auto object-contain mb-4" />
          )}
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight"
            style={{ fontFamily: headingFont }}
          >
            {site.site_title}
          </h1>
          {site.tagline && (
            <p className="text-lg sm:text-xl" style={{ color: 'rgba(255,255,255,0.7)' }}>
              {site.tagline}
            </p>
          )}
          {isEnabled('contact') && (
            <button
              onClick={() => scrollTo('contact')}
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-base font-semibold transition-transform hover:scale-105"
              style={{ background: primary, color: '#fff' }}
              data-testid="hero-cta"
            >
              Book Now <ArrowRight className="w-5 h-5" />
            </button>
          )}
          {socialIcons.length > 0 && (
            <div className="flex items-center justify-center gap-4 pt-4">
              {socialIcons.map(s => (
                <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer" className="opacity-50 hover:opacity-100 transition-opacity">
                  <s.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          )}
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce opacity-40">
          <ChevronDown className="w-6 h-6" />
        </div>
      </section>

      {/* ─── About ─── */}
      {isEnabled('about') && (site.about_title || site.about_content) && (
        <section
          ref={el => (sectionRefs.current.about = el)}
          className="py-20 sm:py-28 px-4"
          style={{ background: ts.sectionBg }}
          data-testid="about-section"
        >
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              {site.about_title && (
                <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: headingFont }}>
                  {site.about_title}
                </h2>
              )}
              {site.about_content && (
                <div className="text-base leading-relaxed whitespace-pre-wrap" style={{ color: ts.textMuted }}>
                  {site.about_content}
                </div>
              )}
              {site.contact_phone && (
                <a href={`tel:${site.contact_phone}`} className="inline-flex items-center gap-2 text-sm" style={{ color: primary }}>
                  <Phone className="w-4 h-4" /> {site.contact_phone}
                </a>
              )}
            </div>
            {site.about_image_url ? (
              <div className="rounded-2xl overflow-hidden aspect-[4/3]" style={{ border: `1px solid ${ts.cardBorder}` }}>
                <img src={resolveUrl(site.about_image_url)} alt="" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="rounded-2xl aspect-[4/3] flex items-center justify-center" style={{ background: ts.cardBg, border: `1px solid ${ts.cardBorder}` }}>
                <Music className="w-20 h-20" style={{ color: `${primary}33` }} />
              </div>
            )}
          </div>
        </section>
      )}

      {/* ─── Packages ─── */}
      {isEnabled('packages') && packages.length > 0 && (
        <section
          ref={el => (sectionRefs.current.packages = el)}
          className="py-20 sm:py-28 px-4"
          style={{ background: ts.sectionAltBg }}
          data-testid="packages-section"
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{ fontFamily: headingFont }}>Packages & Pricing</h2>
              <p className="text-base" style={{ color: ts.textMuted }}>Find the perfect package for your event</p>
            </div>
            <div className={`grid gap-6 ${packages.length === 1 ? 'max-w-md mx-auto' : packages.length === 2 ? 'md:grid-cols-2 max-w-3xl mx-auto' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
              {packages.map((pkg, i) => (
                <div
                  key={pkg.id || i}
                  className="rounded-2xl overflow-hidden transition-transform hover:-translate-y-1"
                  style={{ background: ts.cardBg, border: `1px solid ${ts.cardBorder}` }}
                  data-testid={`package-card-${i}`}
                >
                  {pkg.image_url && (
                    <div className="h-44 overflow-hidden">
                      <img src={resolveUrl(pkg.image_url)} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-6 space-y-4">
                    <h3 className="text-xl font-bold" style={{ fontFamily: headingFont }}>{pkg.name}</h3>
                    <div className="text-3xl font-bold" style={{ color: primary }}>
                      ${(pkg.base_price || 0).toLocaleString()}
                    </div>
                    {pkg.description && (
                      <p className="text-sm" style={{ color: ts.textMuted }}>{pkg.description}</p>
                    )}
                    {pkg.duration_hours && (
                      <p className="text-xs" style={{ color: ts.textMuted }}>Up to {pkg.duration_hours} hours</p>
                    )}
                    {(pkg.features || []).length > 0 && (
                      <ul className="space-y-2 pt-2">
                        {pkg.features.map((f, fi) => (
                          <li key={fi} className="flex items-start gap-2 text-sm" style={{ color: ts.textMuted }}>
                            <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: primary }} />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {isEnabled('contact') && (
                      <button
                        onClick={() => { setContactForm(p => ({ ...p, message: `Interested in: ${pkg.name}` })); scrollTo('contact'); }}
                        className="w-full py-2.5 rounded-xl text-sm font-semibold transition-opacity hover:opacity-90"
                        style={{ background: `${primary}22`, color: primary, border: `1px solid ${primary}44` }}
                      >
                        Get Started
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── Add-Ons ─── */}
      {isEnabled('addons') && addons.length > 0 && (
        <section
          ref={el => (sectionRefs.current.addons = el)}
          className="py-20 sm:py-28 px-4"
          style={{ background: ts.sectionBg }}
          data-testid="addons-section"
        >
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{ fontFamily: headingFont }}>Enhancements & Add-Ons</h2>
              <p className="text-base" style={{ color: ts.textMuted }}>Take your event to the next level</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {addons.map((addon, i) => (
                <div
                  key={addon.id || i}
                  className="rounded-xl p-5 flex items-start gap-4 transition-transform hover:-translate-y-0.5"
                  style={{ background: ts.cardBg, border: `1px solid ${ts.cardBorder}` }}
                  data-testid={`addon-card-${i}`}
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${primary}22` }}>
                    <Star className="w-5 h-5" style={{ color: primary }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm">{addon.name}</h4>
                    {addon.description && (
                      <p className="text-xs mt-1 line-clamp-2" style={{ color: ts.textMuted }}>{addon.description}</p>
                    )}
                    <p className="text-sm font-bold mt-2" style={{ color: primary }}>
                      +${(addon.price || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── Gallery ─── */}
      {isEnabled('gallery') && (site.gallery_images || []).length > 0 && (
        <section
          ref={el => (sectionRefs.current.gallery = el)}
          className="py-20 sm:py-28 px-4"
          style={{ background: ts.sectionAltBg }}
          data-testid="gallery-section"
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{ fontFamily: headingFont }}>Gallery</h2>
              <p className="text-base" style={{ color: ts.textMuted }}>Moments we've created</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {(site.gallery_images || []).map((img, i) => (
                <div
                  key={i}
                  className="aspect-square rounded-xl overflow-hidden group cursor-pointer"
                  style={{ border: `1px solid ${ts.cardBorder}` }}
                >
                  <img
                    src={resolveUrl(img.url)}
                    alt={img.caption || ''}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── Testimonials ─── */}
      {isEnabled('testimonials') && (site.testimonials || []).length > 0 && (
        <section
          ref={el => (sectionRefs.current.testimonials = el)}
          className="py-20 sm:py-28 px-4"
          style={{ background: ts.sectionBg }}
          data-testid="testimonials-section"
        >
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{ fontFamily: headingFont }}>What Our Clients Say</h2>
            </div>
            <div className={`grid gap-6 ${(site.testimonials || []).length === 1 ? 'max-w-lg mx-auto' : 'md:grid-cols-2'}`}>
              {(site.testimonials || []).map((t, i) => (
                <div
                  key={i}
                  className="rounded-2xl p-6 space-y-4"
                  style={{ background: ts.cardBg, border: `1px solid ${ts.cardBorder}` }}
                  data-testid={`testimonial-card-${i}`}
                >
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, si) => (
                      <Star key={si} className="w-4 h-4 fill-current" style={{ color: accent }} />
                    ))}
                  </div>
                  <p className="text-sm leading-relaxed italic" style={{ color: ts.textMuted }}>
                    "{t.text}"
                  </p>
                  <div>
                    <p className="font-semibold text-sm">{t.name}</p>
                    {t.event_type && (
                      <p className="text-xs" style={{ color: ts.textMuted }}>{t.event_type}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── Contact ─── */}
      {isEnabled('contact') && (
        <section
          ref={el => (sectionRefs.current.contact = el)}
          className="py-20 sm:py-28 px-4"
          style={{ background: ts.sectionAltBg }}
          data-testid="contact-section"
        >
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{ fontFamily: headingFont }}>Let's Make It Happen</h2>
              <p className="text-base" style={{ color: ts.textMuted }}>Tell us about your event and we'll get back to you</p>
            </div>

            {submitted ? (
              <div className="text-center py-16 rounded-2xl" style={{ background: ts.cardBg, border: `1px solid ${ts.cardBorder}` }} data-testid="contact-success">
                <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4" style={{ background: `${primary}22` }}>
                  <Check className="w-8 h-8" style={{ color: primary }} />
                </div>
                <h3 className="text-xl font-bold mb-2">Message Sent!</h3>
                <p style={{ color: ts.textMuted }}>We'll get back to you as soon as possible.</p>
              </div>
            ) : (
              <form onSubmit={submitContact} className="rounded-2xl p-6 sm:p-8 space-y-5" style={{ background: ts.cardBg, border: `1px solid ${ts.cardBorder}` }} data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: ts.textMuted }}>Your Name *</label>
                    <input
                      required
                      value={contactForm.name}
                      onChange={e => setContactForm(p => ({ ...p, name: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all focus:ring-2"
                      style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${ts.cardBorder}`, color: ts.textMain, '--tw-ring-color': primary }}
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: ts.textMuted }}>Email *</label>
                    <input
                      required
                      type="email"
                      value={contactForm.email}
                      onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all focus:ring-2"
                      style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${ts.cardBorder}`, color: ts.textMain }}
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: ts.textMuted }}>Phone</label>
                    <input
                      value={contactForm.phone}
                      onChange={e => setContactForm(p => ({ ...p, phone: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all focus:ring-2"
                      style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${ts.cardBorder}`, color: ts.textMain }}
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: ts.textMuted }}>Event Date</label>
                    <input
                      type="date"
                      value={contactForm.event_date}
                      onChange={e => setContactForm(p => ({ ...p, event_date: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all focus:ring-2"
                      style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${ts.cardBorder}`, color: ts.textMain }}
                      data-testid="contact-date-input"
                    />
                  </div>
                </div>
                {event_types.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: ts.textMuted }}>Event Type</label>
                    <select
                      value={contactForm.event_type}
                      onChange={e => setContactForm(p => ({ ...p, event_type: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                      style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${ts.cardBorder}`, color: ts.textMain }}
                      data-testid="contact-event-type-select"
                    >
                      <option value="">Select an event type...</option>
                      {event_types.map((et, i) => (
                        <option key={et.id || i} value={et.name}>{et.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: ts.textMuted }}>Message *</label>
                  <textarea
                    required
                    rows={4}
                    value={contactForm.message}
                    onChange={e => setContactForm(p => ({ ...p, message: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all resize-none focus:ring-2"
                    style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${ts.cardBorder}`, color: ts.textMain }}
                    placeholder="Tell us about your event..."
                    data-testid="contact-message-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3.5 rounded-xl text-base font-semibold transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ background: primary, color: '#fff' }}
                  data-testid="contact-submit-btn"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-5 h-5" /> Send Message</>}
                </button>
              </form>
            )}

            {/* Contact info below form */}
            {(site.contact_email || site.contact_phone) && (
              <div className="flex flex-wrap items-center justify-center gap-6 mt-10" style={{ color: ts.textMuted }}>
                {site.contact_email && (
                  <a href={`mailto:${site.contact_email}`} className="flex items-center gap-2 text-sm hover:underline">
                    <Mail className="w-4 h-4" /> {site.contact_email}
                  </a>
                )}
                {site.contact_phone && (
                  <a href={`tel:${site.contact_phone}`} className="flex items-center gap-2 text-sm hover:underline">
                    <Phone className="w-4 h-4" /> {site.contact_phone}
                  </a>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ─── Footer ─── */}
      <footer
        className="py-10 px-4 text-center"
        style={{ background: ts.sectionBg, borderTop: `1px solid ${ts.cardBorder}` }}
        data-testid="site-footer"
      >
        <div className="max-w-5xl mx-auto space-y-4">
          {socialIcons.length > 0 && (
            <div className="flex items-center justify-center gap-5">
              {socialIcons.map(s => (
                <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer" className="transition-opacity hover:opacity-100" style={{ color: ts.textMuted, opacity: 0.6 }}>
                  <s.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          )}
          <p className="text-xs" style={{ color: ts.textMuted }}>
            &copy; {new Date().getFullYear()} {site.site_title}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
